import { type SidebarProps, Sidebar } from "$root/components/common/Sidebar";
import { qualifier } from "$root/utils";
import { Button, Form, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { Text } from "@mdotm/shadcn/legacy/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import { adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

type FormSidePanelHeaderProps = {
	title: string;
	description: string;
	onCancel?(): void;
};

function FormSidePanelHeader(props: FormSidePanelHeaderProps): React.JSX.Element {
	const { t } = useTranslation();
	return (
		<div className={`border-b border-solid border-b-(--palette-N100) `}>
			<div className="flex items-center px-4 pt-5 pb-2">
				<Text
					type="Body/XL/Bold"
					as="p"
					className="grow h-fit line-clamp-2"
					data-qualifier={qualifier.component.sidePanelHeader.title}
				>
					{props.title}
				</Text>
				<div className="flex justify-end space-x-4 bg-white items-center">
					<Button
						size="small"
						palette="tertiary"
						onClick={props.onCancel}
						data-qualifier={qualifier.component.sidePanelHeader.cancel}
					>
						{t("BUTTON.CANCEL")}
					</Button>
					<SubmitButton size="small" palette="primary" data-qualifier={qualifier.component.sidePanelHeader.submit}>
						{t("BUTTON.APPLY")}
					</SubmitButton>
				</div>
			</div>
			<Text type="Body/M/Book" className="px-4 pb-4" color={themeCSSVars.palette_N700} as="p">
				{props.description}
			</Text>
		</div>
	);
}

export type FormSidebarContentProps = {
	title: string;
	description: string;
	onSubmit(): MaybePromise<void>;
	onCancel(): void;
	children: ReactNode;
	footer?: ReactNode;
};

export function FormSidebarContent(props: FormSidebarContentProps): React.JSX.Element {
	// bg-[color:var(--palette-N20)]
	return (
		<Form onSubmitAsync={props.onSubmit} className="flex-1 min-h-0 flex flex-col h-dvh bg-white pb-4">
			<FormSidePanelHeader title={props.title} description={props.description} onCancel={props.onCancel} />
			<div className={`flex-1 min-h-0 flex flex-col px-4 pt-4 border-t border-solid border-t-(--palette-N100)`}>
				{props.children}
			</div>
			{props.footer && <div className="px-4 pt-4 border-t border-solid border-t-(--palette-N100)">{props.footer}</div>}
		</Form>
	);
}

export type SidePanelChildrenProps<T = void> = {
	setCanDismiss(v: boolean): void;
	resolve(v: T): void;
	reject(err?: unknown): void;
};

export function spawnSidePanel<T = void>(
	params: {
		signal?: AbortSignal;
		children: (props: SidePanelChildrenProps<T>) => ReactNode;
	} & Pick<SidebarProps, "className" | "style" | "position">,
): Promise<T> {
	const canDismissRef = { current: true };
	const setCanDismiss = (v: boolean) => {
		canDismissRef.current = v;
	};
	return spawn<T>(
		adaptAnimatedNodeProvider(({ onHidden, reject, resolve, show }) => (
			<Sidebar
				className={params.className}
				style={params.style}
				position={params.position}
				open={show}
				onClose={() => (canDismissRef.current ? reject() : undefined)}
				width={440}
				mode="modal"
				onAnimationStateChange={(s) => s === "exited" && onHidden()}
			>
				{params.children({ setCanDismiss, resolve, reject })}
			</Sidebar>
		)),
		{ signal: params.signal },
	).promise;
}
